import { useMemo, useState } from "react";
import { RoleShell } from "@/components/RoleShell";
import { useAppointments } from "@/hooks/useAppointments";
import { useBranch } from "@/contexts/BranchContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Armchair, Sparkles, Square, Clock } from "lucide-react";
import { toast } from "sonner";
import { FLOORMAP } from "@/constants/testIds";

function elapsed(startedAt) {
  if (!startedAt) return "";
  const ms = Date.now() - new Date(startedAt).getTime();
  const m = Math.floor(ms / 60000);
  return `${m}m`;
}

export default function FloorMap() {
  const { activeBranchId, activeBranch } = useBranch();
  const { items, update } = useAppointments({ branchId: activeBranchId });
  const { settings } = useSettings();

  const chairs = useMemo(() => {
    const cnt = activeBranch?.chair_count || 6;
    return Array.from({ length: cnt }).map((_, i) => `Chair ${i + 1}`);
  }, [activeBranch]);
  const rooms = useMemo(() => {
    const cnt = activeBranch?.room_count || 3;
    return Array.from({ length: cnt }).map((_, i) => `Room ${String.fromCharCode(65 + i)}`);
  }, [activeBranch]);
  const stations = ["Nail Station"];

  const tiles = [...chairs.map((n) => ({ name: n, kind: "chair" })),
                 ...rooms.map((n) => ({ name: n, kind: "room" })),
                 ...stations.map((n) => ({ name: n, kind: "station" }))];

  const statusFor = (name) => {
    const inService = items.find((a) => a.status === "in-service" && a.chair_room === name);
    if (inService) return { type: "busy", appointment: inService };
    const completedRecent = items
      .filter((a) => a.status === "completed" && a.chair_room === name && a.completed_at)
      .sort((x, y) => new Date(y.completed_at) - new Date(x.completed_at))[0];
    if (completedRecent) {
      const min = (Date.now() - new Date(completedRecent.completed_at).getTime()) / 60000;
      if (min < settings.buffer_minutes_default) return { type: "cleaning", appointment: completedRecent, minLeft: Math.ceil(settings.buffer_minutes_default - min) };
    }
    const nextBooked = items.find((a) => a.status === "booked" && a.chair_room === name);
    return { type: "free", nextBooked };
  };

  const checkedIn = items.filter((a) => a.status === "checked-in" && !a.chair_room);

  const [pickTile, setPickTile] = useState(null);

  const assignToTile = async (clientId) => {
    if (!pickTile) return;
    await update(clientId, { chair_room: pickTile });
    toast.success(`Assigned to ${pickTile}`);
    setPickTile(null);
  };

  return (
    <RoleShell title="Floor Map">
      <div className="max-w-[1700px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6" data-testid={FLOORMAP.root}>
        <div>
          <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="overline mb-1">Real-time floor</div>
              <h1 className="font-display font-bold text-[28px] tracking-[-0.02em]">{activeBranch?.name || "—"}</h1>
            </div>
            <div className="flex items-center gap-3 font-inter text-[12.5px] text-[var(--color-text-muted)]">
              <Legend color="#16A34A" label="Free" />
              <Legend color="#D4A853" label="In service" />
              <Legend color="#F59E0B" label="Cleaning" />
              <Legend color="#B91C1C" label="Unavailable" />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {tiles.map((t) => {
              const s = statusFor(t.name);
              const Icon = t.kind === "chair" ? Armchair : (t.kind === "room" ? Square : Sparkles);
              const palette = s.type === "busy" ? { bg: "linear-gradient(180deg,#F0D898,#D4A853)", border: "#B5892E", text: "#FFFFFF" }
                            : s.type === "cleaning" ? { bg: "linear-gradient(180deg,#FEF3C7,#FCD34D)", border: "#D97706", text: "#7C2D12" }
                            : { bg: "linear-gradient(180deg,#D1FAE5,#A7F3D0)", border: "#16A34A", text: "#064E3B" };
              return (
                <button
                  key={t.name}
                  data-testid={FLOORMAP.tile(t.name.replace(/\s+/g, "-"))}
                  onClick={() => { if (s.type === "free") setPickTile(t.name); }}
                  className="rounded-[22px] p-5 text-left transition-all duration-200 hover:-translate-y-1"
                  style={{
                    background: palette.bg,
                    border: `2px solid ${palette.border}`,
                    color: palette.text,
                    minHeight: 160,
                    boxShadow: "0 12px 24px -10px rgba(28,25,23,0.18), inset 0 1px 0 0 rgba(255,255,255,0.5), inset 0 -1px 0 0 rgba(0,0,0,0.05)",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon size={18} />
                      <span className="font-sora font-semibold text-[14px]">{t.name}</span>
                    </div>
                    <span className="text-[10px] font-sora font-bold uppercase tracking-[0.2em]" style={{ background: "rgba(255,255,255,0.35)", padding: "3px 8px", borderRadius: 999 }}>
                      {s.type}
                    </span>
                  </div>
                  {s.type === "busy" && (
                    <div className="mt-4">
                      <div className="font-mono text-[24px] leading-none">{s.appointment.token_number}</div>
                      <div className="font-sora font-semibold text-[14px] mt-1.5 truncate">{s.appointment.client_name}</div>
                      <div className="font-inter text-[12px] mt-0.5 truncate" style={{ opacity: 0.9 }}>{(s.appointment.services_requested || [])[0]}</div>
                      <div className="font-inter text-[11.5px] mt-2 flex items-center gap-1 opacity-90"><Clock size={11} /> {elapsed(s.appointment.service_started_at)} elapsed</div>
                    </div>
                  )}
                  {s.type === "cleaning" && (
                    <div className="mt-4">
                      <div className="font-sora font-semibold text-[13px]">Cleaning</div>
                      <div className="font-inter text-[12px] opacity-80 mt-1">~{s.minLeft} min until ready</div>
                    </div>
                  )}
                  {s.type === "free" && (
                    <div className="mt-4">
                      <div className="font-sora font-semibold text-[13px]">Available</div>
                      {s.nextBooked ? (
                        <div className="font-inter text-[12px] opacity-80 mt-1">Next: {s.nextBooked.token_number} · {s.nextBooked.client_name}</div>
                      ) : (
                        <div className="font-inter text-[12px] opacity-80 mt-1">Tap to assign</div>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Side panel */}
        <aside className="glass-primary rounded-[20px] p-5 h-fit lg:sticky lg:top-[100px]">
          <div className="overline mb-3">Checked-in · unassigned</div>
          {checkedIn.length === 0 ? (
            <div className="font-inter text-[13px] text-[var(--color-text-muted)]">Everyone is seated ✨</div>
          ) : (
            <div className="space-y-2">
              {checkedIn.map((a) => (
                <button
                  key={a.id}
                  onClick={() => pickTile && assignToTile(a.id)}
                  className={`w-full text-left rounded-xl p-3 border transition ${pickTile ? "bg-[var(--color-gold-mid)]/10 border-[var(--color-gold-mid)]/40 cursor-pointer" : "bg-white/55 border-white/80"}`}
                >
                  <div className="font-mono text-[13.5px] text-[var(--color-mauve-deep)]">{a.token_number}</div>
                  <div className="font-sora font-semibold text-[13px] mt-0.5 truncate">{a.client_name}</div>
                  <div className="font-inter text-[11.5px] text-[var(--color-text-muted)] truncate">{(a.services_requested || []).join(", ")}</div>
                </button>
              ))}
            </div>
          )}
          {pickTile && (
            <div className="mt-3 p-3 rounded-xl glass-subtle font-inter text-[12.5px] text-[var(--color-text-secondary)]">
              Tap a client above to assign them to <span className="font-sora font-semibold text-[var(--color-mauve-deep)]">{pickTile}</span>.
              <button className="block mt-2 font-inter text-[12px] text-[var(--color-text-muted)]" onClick={() => setPickTile(null)}>Cancel</button>
            </div>
          )}
        </aside>
      </div>
    </RoleShell>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-3 h-3 rounded-full" style={{ background: color }} />
      <span>{label}</span>
    </div>
  );
}
